import { useMemo, useState } from "react";
import type { BaselineComparisonItem, PersonalBaselineResult } from "../personalBaseline";

type PersonalBaselineCardProps = {
  baseline: PersonalBaselineResult;
};

function titleCase(value: string): string {
  return value.replaceAll("_", " ").split(" ").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function formatMetricName(metricName: BaselineComparisonItem["metricName"]): string {
  switch (metricName) {
    case "variability_index":
      return "Variability index";
    case "aerobic_decoupling":
      return "Aerobic decoupling";
    case "normalized_power":
      return "Normalized power";
    case "intensity_factor":
      return "Intensity factor";
    case "first_half_vs_second_half_power":
      return "First-half vs second-half power";
    case "durability60":
      return "Durability 60 min";
    case "durability90":
      return "Durability 90 min";
    case "durability120":
      return "Durability 120 min";
    case "cadence_stability":
      return "Cadence stability";
    case "average_minute_ventilation":
      return "Average minute ventilation";
    case "maximum_minute_ventilation":
      return "Maximum minute ventilation";
    case "average_breathing_frequency":
      return "Average breathing frequency";
    case "respiratory_drift":
      return "Respiratory drift";
    case "ventilation_to_power_relationship":
      return "Ventilation-to-power relationship";
    case "respiratory_coverage":
      return "Respiratory coverage";
    default:
      return metricName;
  }
}

function formatValue(metric: BaselineComparisonItem): string {
  const digits = metric.metricName === "variability_index" || metric.metricName === "intensity_factor" || metric.metricName === "ventilation_to_power_relationship" ? 2 : 1;
  const suffix = metric.metricName.includes("power") && !metric.metricName.includes("ventilation") ? " W" : metric.metricName.includes("coverage") || metric.metricName.includes("decoupling") || metric.metricName.includes("drift") ? "%" : "";
  return `${metric.currentValue.toFixed(digits)}${suffix}`;
}

function formatBaselineValue(metric: BaselineComparisonItem): string {
  const digits = metric.metricName === "variability_index" || metric.metricName === "intensity_factor" || metric.metricName === "ventilation_to_power_relationship" ? 2 : 1;
  const suffix = metric.metricName.includes("power") && !metric.metricName.includes("ventilation") ? " W" : metric.metricName.includes("coverage") || metric.metricName.includes("decoupling") || metric.metricName.includes("drift") ? "%" : "";
  return `${metric.personalBaseline.toFixed(digits)}${suffix}`;
}

export function PersonalBaselineCard({ baseline }: PersonalBaselineCardProps) {
  const [showAllMetrics, setShowAllMetrics] = useState(false);
  const [showComparableRides, setShowComparableRides] = useState(false);
  const primaryComparisons = useMemo(() => baseline.topComparisons.slice(0, 5), [baseline.topComparisons]);

  return (
    <section className="personal-baseline-card" aria-labelledby="personal-baseline-title">
      <div className="personal-baseline-header">
        <div>
          <p className="eyebrow">PERSONAL BASELINE</p>
          <h2 id="personal-baseline-title">Personal Baseline</h2>
          {baseline.insufficientHistory ? (
            <p className="command-note">Johan needs at least three comparable completed rides to establish this personal baseline.</p>
          ) : (
            <p className="command-note">Based on {baseline.comparableRideCount} comparable rides · window {titleCase(baseline.comparisonWindow ?? "all")}</p>
          )}
        </div>
        <span className={`coach-summary-badge confidence-${baseline.baselineConfidence}`}>{titleCase(baseline.baselineConfidence)} confidence</span>
      </div>

      {!baseline.insufficientHistory && primaryComparisons.length > 0 ? (
        <div className="personal-baseline-grid">
          {primaryComparisons.map(metric => (
            <article key={metric.metricName} className="personal-baseline-item">
              <h3>{formatMetricName(metric.metricName)}</h3>
              <p className="baseline-value">{formatValue(metric)}</p>
              <p className="command-note">Typical baseline: {formatBaselineValue(metric)}</p>
              <p>{metric.interpretation}</p>
              <p className="command-note">Confidence: {titleCase(metric.confidence)} · Comparable rides: {metric.comparableRideCount}</p>
            </article>
          ))}
        </div>
      ) : null}

      {!baseline.insufficientHistory && baseline.allComparisons.length > 0 ? (
        <details className="analysis-source-summary" open={showAllMetrics}>
          <summary onClick={event => {
            event.preventDefault();
            setShowAllMetrics(current => !current);
          }}>
            {showAllMetrics ? "Hide all compared metrics" : "Show all compared metrics"}
          </summary>
          {showAllMetrics ? (
            <ul className="baseline-all-metrics-list">
              {baseline.allComparisons.map(item => (
                <li key={item.metricName}>
                  <strong>{formatMetricName(item.metricName)}</strong>
                  <span>{formatValue(item)} vs {formatBaselineValue(item)} · {item.interpretation}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </details>
      ) : null}

      <details className="analysis-source-summary" open={showComparableRides}>
        <summary onClick={event => {
          event.preventDefault();
          setShowComparableRides(current => !current);
        }}>
          Comparable rides ({baseline.comparableRides.length})
        </summary>
        {showComparableRides ? (
          <ul className="baseline-comparable-rides-list">
            {baseline.comparableRides.map(ride => (
              <li key={ride.activityId}>
                <strong>{new Date(ride.activityDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</strong>
                <span>Score {ride.score} · {ride.activityId}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </details>
    </section>
  );
}
